import React from "react";
import { Button } from "antd";
import {
    CheckCircleOutlined,
    LockOutlined,
    LoginOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { Tokens } from "Shared/Tokens";

export const redirectToLogin = (returnPath) => {
    const { origin } = window.location;
    const rawPath =
        returnPath ||
        window.location.pathname + (window.location.search || "") ||
        "/portfolios";
    const separator = rawPath.includes("?") ? "&" : "?";
    const returnTo = `${origin}${rawPath}${
        rawPath.includes("login=1") ? "" : `${separator}login=1`
    }`;
    window.location.href = `https://auth.rajchandra.me/login?returnTo=${encodeURIComponent(
        returnTo
    )}`;
};

const DEFAULT_FEATURES = [
    "Live Binance Spot, Simple Earn, and Funding wallet sync",
    "Audited Account Summary, USDT Ledger, and Tradebook",
    "Ask AI portfolio strategist & cross-device cloud backup",
];

function LoginWall({
    title = "Sign in to access this feature",
    description = "Connect your account to unlock live Binance synchronization, audited P&L ledgers, and AI portfolio insights.",
    features = DEFAULT_FEATURES,
    returnPath,
    compact = false,
    showOverviewAction = true,
    ctaLabel = "Sign in to continue",
    testId,
}) {
    const history = useHistory();

    const handleLogin = () => {
        redirectToLogin(returnPath);
    };

    if (compact) {
        return (
            <div
                data-testid={testId || "guest-login-banner"}
                style={{
                    background: Tokens.surface,
                    border: `1px solid ${Tokens.border}`,
                    borderRadius: Tokens.radiusLg,
                    padding: "16px 18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    flexWrap: "wrap",
                    boxShadow: Tokens.shadowSm,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        flex: "1 1 240px",
                        minWidth: 0,
                    }}
                >
                    <div
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: Tokens.radiusMd,
                            background: Tokens.accentSubtle,
                            border: `1px solid ${Tokens.accentBorder}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <LockOutlined
                            style={{ fontSize: 17, color: Tokens.accent }}
                        />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: Tokens.textPrimary,
                                lineHeight: 1.35,
                                marginBottom: 3,
                            }}
                        >
                            {title}
                        </div>
                        <div
                            style={{
                                fontSize: 12.5,
                                color: Tokens.textMuted,
                                lineHeight: 1.45,
                            }}
                        >
                            {description}
                        </div>
                    </div>
                </div>
                <Button
                    type="primary"
                    icon={<LoginOutlined />}
                    data-testid="guest-login-banner-btn"
                    onClick={handleLogin}
                    style={{
                        height: 36,
                        padding: "0 16px",
                        borderRadius: Tokens.radiusSm,
                        fontWeight: 600,
                        fontSize: 13,
                        flexShrink: 0,
                    }}
                >
                    Sign in
                </Button>
            </div>
        );
    }

    const featureItems =
        Array.isArray(features) && features.length > 0
            ? features
            : DEFAULT_FEATURES;

    return (
        <div
            data-testid={testId || "guest-login-wall"}
            style={{
                maxWidth: 460,
                margin: "32px auto",
                padding: "28px 24px",
                background: Tokens.surface,
                border: `1px solid ${Tokens.border}`,
                borderRadius: Tokens.radiusLg,
                boxShadow: Tokens.shadowSm,
                textAlign: "center",
            }}
        >
            <div
                style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: Tokens.accentSubtle,
                    border: `1px solid ${Tokens.accentBorder}`,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 16,
                }}
            >
                <SafetyCertificateOutlined
                    style={{ fontSize: 24, color: Tokens.accent }}
                />
            </div>

            <div
                style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: Tokens.textPrimary,
                    letterSpacing: "-0.01em",
                    marginBottom: 8,
                }}
            >
                {title}
            </div>

            <div
                style={{
                    fontSize: 13.5,
                    color: Tokens.textSecondary,
                    lineHeight: 1.55,
                    marginBottom: 20,
                }}
            >
                {description}
            </div>

            <div
                style={{
                    textAlign: "left",
                    background: Tokens.surfaceElevated,
                    border: `1px solid ${Tokens.borderSubtle}`,
                    borderRadius: Tokens.radiusMd,
                    padding: "12px 14px",
                    marginBottom: 22,
                    display: "flex",
                    flexDirection: "column",
                    gap: 9,
                }}
            >
                {featureItems.map((item) => (
                    <div
                        key={item}
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 9,
                            fontSize: 12.5,
                            color: Tokens.textSecondary,
                            lineHeight: 1.4,
                        }}
                    >
                        <CheckCircleOutlined
                            style={{
                                color: Tokens.accent,
                                fontSize: 13,
                                marginTop: 2,
                                flexShrink: 0,
                            }}
                        />
                        <span>{item}</span>
                    </div>
                ))}
            </div>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                }}
            >
                <Button
                    type="primary"
                    icon={<LoginOutlined />}
                    data-testid="login-wall-cta-btn"
                    onClick={handleLogin}
                    style={{
                        height: 42,
                        borderRadius: Tokens.radiusMd,
                        fontWeight: 600,
                        fontSize: 14,
                        width: "100%",
                    }}
                >
                    {ctaLabel}
                </Button>

                {showOverviewAction && (
                    <Button
                        type="default"
                        data-testid="login-wall-overview-btn"
                        onClick={() => history.push("/portfolios")}
                        style={{
                            height: 38,
                            borderRadius: Tokens.radiusMd,
                            fontSize: 13,
                            fontWeight: 500,
                            color: Tokens.textSecondary,
                            borderColor: Tokens.border,
                            background: "transparent",
                            width: "100%",
                        }}
                    >
                        Manage local portfolios
                    </Button>
                )}
            </div>
        </div>
    );
}

export default LoginWall;
